/* eslint-disable @typescript-eslint/no-explicit-any */
import { useServices } from "@/services/hooks/useServices";
import { Label } from "@/services/label.service";
import {
  PostmasterConnectionStatus,
  PostmasterDnsRecordsResponse,
  PostmasterDomainsResponse,
  SpamRateResponse,
} from "@/types/postman.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { SUBSCRIPTION_QUERY_KEY } from "./use-subscription";

export const useGetDomainsWithLatestData = ({
  filters,
  enabled = true,
}: {
  filters: {
    page: number;
    limit: number;
    search?: string;
    isSynced?: boolean;
    labels?: string[];
  };
  enabled?: boolean;
}) => {
  const { postmasterService } = useServices();

  const { page, limit, search, isSynced, labels } = filters;

  const memoizedFilters = useMemo(() => {
    return {
      page,
      limit,
      search,
      isSynced,
      labels,
    };
  }, [page, limit, search, isSynced, labels]);

  return useQuery<PostmasterDomainsResponse>({
    queryKey: ["postmaster-domains-latest", memoizedFilters],
    queryFn: () =>
      postmasterService.getDomainsWithLatestData(
        memoizedFilters.page,
        memoizedFilters.limit,
        memoizedFilters.search,
        memoizedFilters.isSynced,
        memoizedFilters.labels
      ),
    select: (data) => ({
      domains: data.domains,
      stats: data.stats,
      pagination: {
        total: data.pagination.total,
        page: data.pagination.page,
        limit: data.pagination.limit,
        pages: data.pagination.pages,
      },
    }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetIsConnected = () => {
  const { postmasterService } = useServices();

  return useQuery<boolean>({
    queryKey: ["is-Connected"],
    queryFn: () => postmasterService.isConnected(),
  });
};

export const useDisconnect = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => postmasterService.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["postmaster-connected"] });
    },
  });
};

export const useGetDnsRecordsWithStats = ({
  page,
  limit,
  search,
  labels,
  selectedEspTypes,
}: {
  page: number;
  limit: number;
  search?: string;
  labels?: string[];
  selectedEspTypes?: string[];
}) => {
  const { postmasterService } = useServices();

  const filters = useMemo(() => {
    return {
      page,
      limit,
      search,
      labels,
      selectedEspTypes,
    };
  }, [page, limit, search, labels, selectedEspTypes]);

  return useQuery<PostmasterDnsRecordsResponse>({
    queryKey: ["postmaster-dns-records-with-stats", filters],
    queryFn: () =>
      postmasterService.getDnsRecordsWithStats(
        filters.page,
        filters.limit,
        filters.search,
        filters.labels,
        filters.selectedEspTypes
      ),
    // select: (data) => ({
    //   records: data.records.map((record) => ({
    //     ...record,
    //     lastChecked: record.lastChecked,
    //     formattedLastChecked: record.lastChecked,
    //   })),
    //   stats: data.stats,
    //   pagination: data.pagination,
    // }),
    refetchOnWindowFocus: false,
  });
};

export const useGetSpamRateData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery<SpamRateResponse>({
    queryKey: ["postmaster-spam-rate", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getSpamRateData(domain, startDate, endDate),
  });
};

export const useGetIpReputationData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-ip-reputation", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getIpReputationData(domain, startDate, endDate),
  });
};

export const useGetDomainReputationData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-domain-reputation", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getDomainReputationData(domain, startDate, endDate),
  });
};

export const useGetAuthenticationData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-authentication", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getAuthenticationData(domain, startDate, endDate),
  });
};

export const useGetEncryptionData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-encryption", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getEncryptionData(domain, startDate, endDate),
  });
};

export const useGetDeliveryErrorData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-delivery-error", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getDeliveryErrorData(domain, startDate, endDate),
  });
};

export const useGetFeedbackLoopData = ({
  domain,
  startDate,
  endDate,
}: {
  domain: string;
  startDate: Date;
  endDate: Date;
}) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-feedback-loop", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getFeedbackLoopData(domain, startDate, endDate),
  });
};

interface UseGetComplianceDataParams {
  domain: string;
  startDate?: Date;
  endDate?: Date;
}

export const useGetComplianceData = ({
  domain,
  startDate,
  endDate,
}: UseGetComplianceDataParams) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-compliance", { domain, startDate, endDate }],
    queryFn: () =>
      postmasterService.getComplianceData(domain, startDate, endDate),
  });
};

export const useGetDnsCheckData = (domain: string) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-dns-check", domain],
    queryFn: () => postmasterService.getDnsCheckData(domain),
    enabled: !!domain, // Only run query when domain is provided
  });
};

export const useGetConnectionStatus = () => {
  const { postmasterService } = useServices();

  return useQuery<PostmasterConnectionStatus>({
    queryKey: ["postmaster-connection-status"],
    queryFn: () => postmasterService.getConnectionStatus(),
  });
};

export const useConnectPostmaster = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { url, accountId } = await postmasterService.generateAuthUrl();

      // Create promise to handle the OAuth callback
      const authPromise = new Promise((resolve, reject) => {
        const popup = window.open(
          url,
          "Google Postmaster Auth",
          `width=600,height=600,left=${window.screen.width / 2 - 300},top=${
            window.screen.height / 2 - 300
          }`
        );

        const intervalId = setInterval(() => {
          try {
            if (!popup || popup.closed) {
              clearInterval(intervalId);
              reject(new Error("Popup closed"));
              return;
            }

            const popupUrl = popup.location.href;
            const urlObj = new URL(popupUrl);
            const code = urlObj.searchParams.get("code");

            if (code) {
              clearInterval(intervalId);
              popup.close();
              postmasterService
                .handleAuthCallback(code, accountId)
                .then(resolve)
                .catch(reject);
            }
          } catch (e) {
            // Ignore cross-origin errors
          }
        }, 500);
      });

      return await authPromise;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["postmaster-connection-status"],
      });
    },
  });
};

export const useDisconnectPostmaster = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) =>
      postmasterService.disconnectAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["postmaster-connection-status"],
      });
    },
  });
};

// Separate hook for fetching stored DNS records
export const useGetStoredDnsRecords = (domain: string, espType: string) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["stored-dns-records", domain, espType],
    queryFn: () => postmasterService.getStoredDnsRecords(domain, espType),
    enabled: !!domain,
    refetchOnWindowFocus: false,
  });
};

// Separate hook for running new DNS check
export const useRunDnsCheck = (domain: string) => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: { domain: string; espType?: string }) => 
      postmasterService.runDomainHealthCheck(params?.domain || domain, params?.espType),
    onSuccess: () => {
      // Invalidate the stored records query to refetch latest data
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY],
      });
    },
  });
};

export const useEmailHealthTestStatus = (domain: string, enabled: boolean) => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["email-health-test-status", domain],
    queryFn: () => postmasterService.getEmailTestStatus(domain),
    refetchOnWindowFocus: false,
    refetchInterval: 10000, // 10 seconds
    enabled: !!domain && enabled,
  });
};

export const useUpdateDomainsSyncStatus = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domains,
      isSynced,
    }: {
      domains: string[];
      isSynced: boolean;
    }) => postmasterService.updateDomainsSyncStatus(domains, isSynced),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["postmaster-domains-latest"],
      });
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY],
      });
    },
  });
};

export const useAddLabelsToPostmasterDomain = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domain,
      labelIds,
    }: {
      domain: string[];
      labelIds: string[];
    }) => postmasterService.addLabelsToPostmasterDomain(domain, labelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["postmaster-domains-latest"],
      });
    },
  });
};

export const useRemoveLabelsFromPostmasterDomains = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domains,
      labelIds,
    }: {
      domains: string[];
      labelIds: string[];
    }) => postmasterService.removeLabelsFromDomains(domains, labelIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["postmaster-domains-latest"],
      });
    },
  });
};
export const useAddDomainToDns = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domain,
      labelIds,
    }: {
      domain: string[];
      labelIds: string[];
      pageMetadata?: {
        page: number;
        limit: number;
        search: string;
        selectedLabels: string[];
      };
    }) => postmasterService.addDomainToDns(domain, labelIds),
    onSuccess: (_data, { domain, labelIds, pageMetadata }) => {
      try {
        if (!pageMetadata || domain.length > 10) {
          queryClient.invalidateQueries({
            queryKey: ["postmaster-dns-records-with-stats"],
          });
          return;
        }

        // Optimistic update
        const labelsFromCache = queryClient.getQueryData<{ data: Label[] }>([
          "labels",
          {
            page: 1,
            limit: 100,
            labelType: "dns",
          },
        ]);

        const lookupMap = new Map<
          string,
          Pick<Label, "_id" | "name" | "description" | "color">
        >();

        // update lookup map only for add operation
        labelsFromCache?.data.forEach((label) => {
          if (labelIds.includes(label._id)) {
            lookupMap.set(label._id, {
              _id: label._id,
              name: label.name,
              description: label.description,
              color: label.color,
            });
          }
        });

        queryClient.setQueryData(
          [
            "postmaster-dns-records-with-stats",
            {
              page: pageMetadata.page,
              limit: pageMetadata.limit,
              search: pageMetadata.search,
              labels:
                pageMetadata.selectedLabels.length > 0
                  ? pageMetadata.selectedLabels
                  : undefined,
            },
          ],
          (oldData: { records: any[] }) => {
            const updatedData = oldData?.records?.map((dnsRecord: any) => {
              if (domain.includes(dnsRecord.domain)) {
                // check if updated label is in selected labels then return null
                const currentLabelsMap = new Map(
                  // Null check for dnsRecord.labels
                  (dnsRecord.labels || []).map((label: any) => [
                    label._id,
                    label,
                  ])
                );

                labelIds.forEach((labelId) => {
                  const label = lookupMap.get(labelId);
                  if (label) {
                    currentLabelsMap.set(label._id, label);
                  }
                });

                return {
                  ...dnsRecord,
                  labels: Array.from(currentLabelsMap.values()),
                };
              }
              return dnsRecord;
            });

            return {
              ...oldData,
              records: updatedData,
            };
          }
        );
      } catch (error) {
        // on error invalidate the query to refetch latest data
        queryClient.invalidateQueries({
          queryKey: ["postmaster-dns-records-with-stats"],
        });
        console.error("Failed optimistic update", error);
      }
    },
  });
};

export const useRemoveDomainFromDns = () => {
  const { postmasterService } = useServices();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      domain,
      labelIds,
    }: {
      domain: string[];
      labelIds: string[];
      pageMetadata?: {
        page: number;
        limit: number;
        search: string;
        selectedLabels: string[];
      };
    }) => postmasterService.removeDomainFromDns(domain, labelIds),
    onSuccess: (_data, { domain, labelIds, pageMetadata }) => {
      try {
        if (!pageMetadata || domain.length > 10) {
          queryClient.invalidateQueries({
            queryKey: ["postmaster-dns-records-with-stats"],
          });
          return;
        }

        // Optimistic update
        queryClient.setQueryData(
          [
            "postmaster-dns-records-with-stats",
            {
              page: pageMetadata.page,
              limit: pageMetadata.limit,
              search: pageMetadata.search,
              labels:
                pageMetadata.selectedLabels.length > 0
                  ? pageMetadata.selectedLabels
                  : undefined,
            },
          ],
          (oldData: { records: any[] }) => {
            const updatedData = oldData?.records
              ?.map((dnsRecord: any) => {
                if (domain.includes(dnsRecord.domain)) {
                  // check if updated label is in selected labels then return null
                  if (
                    pageMetadata?.selectedLabels.length > 0 &&
                    pageMetadata?.selectedLabels.some((id) =>
                      labelIds.includes(id)
                    )
                  ) {
                    return null;
                  }

                  const updatedLabels = dnsRecord.labels.filter(
                    (label: any) => {
                      return !labelIds.includes(label._id);
                    }
                  );

                  return {
                    ...dnsRecord,
                    labels: updatedLabels,
                  };
                }
                return dnsRecord;
              })
              .filter((dnsRecord: any) => dnsRecord !== null);

            return {
              ...oldData,
              records: updatedData,
            };
          }
        );
      } catch (error) {
        // on error invalidate the query to refetch latest data
        queryClient.invalidateQueries({
          queryKey: ["postmaster-dns-records-with-stats"],
        });
        console.error("Failed optimistic update", error);
      }
    },
  });
};
export const useGetStats = () => {
  const { postmasterService } = useServices();

  return useQuery({
    queryKey: ["postmaster-stats"],
    queryFn: () => postmasterService.getStats(),
  });
};

// ------------------------- Publish Report -------------------------

export function usePublishReport() {
  const { postmasterService } = useServices();

  return useMutation({
    mutationFn: async (payload: { domain: string; reportIds: string[] }) => {
      return postmasterService.publishReport(payload.domain, payload.reportIds);
    },
  });
}

export function useUnPublishReport() {
  const { postmasterService } = useServices();

  return useMutation({
    mutationFn: async (payload: { domain: string; reportIds: string[] }) => {
      return postmasterService.unPublishReport(
        payload.domain,
        payload.reportIds
      );
    },
  });
}

export function usePublishDomainReport() {
  const { postmasterService } = useServices();

  return useMutation({
    mutationFn: async (payload: { domains: string[] }) => {
      return postmasterService.publishDomainReport(payload.domains);
    },
  });
}
export function useUnPublishDomainReport() {
  const { postmasterService } = useServices();

  return useMutation({
    mutationFn: async (payload: { domains: string[] }) => {
      return postmasterService.unPublishDomainReport(payload.domains);
    },
  });
}
